
import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { RecipeCard } from "./RecipeCard";
import { DietaryRestriction, HealthyAlternative } from "@/types/dietary";
import { dietaryRestrictions } from "@/data/dietaryRestrictions";
import { DietaryOption } from "./dietary/DietaryOption";
import { DietarySelectorDropdown } from "./dietary/DietarySelectorDropdown";

interface RecipeFunFactsProps {
  ingredients: string[];
  healthyAlternatives?: HealthyAlternative[];
  onDietaryChange: (alternatives: HealthyAlternative[]) => void;
}

export const RecipeFunFacts = ({ 
  ingredients, 
  healthyAlternatives = [],
  onDietaryChange
}: RecipeFunFactsProps) => {
  const [selectedDiet, setSelectedDiet] = useState<DietaryRestriction | null>(null);

  const relevantDiets = useMemo(() => {
    return dietaryRestrictions
      .map(diet => {
        const hasMatchingIngredients = diet.keywords.some(keyword =>
          ingredients.some(ingredient =>
            ingredient.toLowerCase().includes(keyword.toLowerCase())
          )
        );

        if (!hasMatchingIngredients) {
          return null;
        }

        const relevantAlternatives = diet.alternatives.filter(alt =>
          alt.keywords.some(keyword =>
            ingredients.some(ingredient =>
              ingredient.toLowerCase().includes(keyword.toLowerCase())
            )
          )
        );

        if (relevantAlternatives.length === 0) {
          return null;
        }

        return {
          ...diet,
          alternatives: relevantAlternatives
        };
      })
      .filter((diet): diet is DietaryRestriction => diet !== null);
  }, [ingredients]);

  const handleDietSelect = (diet: DietaryRestriction | null) => {
    setSelectedDiet(diet);
    if (!diet) {
      onDietaryChange([]);
      return;
    }
    
    const alternatives = diet.alternatives.map(alt => ({
      original: alt.original,
      alternative: alt.replacement,
      note: alt.note
    }));
    
    onDietaryChange(alternatives);
  };

  return (
    <RecipeCard title="Dietary Options" icon="🥗">
      <div className="space-y-6">
        {relevantDiets.length > 0 ? (
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <span className="text-2xl">🍽️</span> 
              Special Diet Helper
            </h3>
            
            <DietarySelectorDropdown
              selectedDiet={selectedDiet}
              relevantDiets={relevantDiets}
              onDietSelect={handleDietSelect}
              allowNoDiet={true}
            />

            {selectedDiet && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4 space-y-3"
              >
                {selectedDiet.alternatives.map((alt, index) => (
                  <DietaryOption key={index} alternative={alt} index={index} />
                ))}
              </motion.div>
            )}
          </div>
        ) : (
          <div className="text-muted-foreground text-sm italic p-4 bg-gray-50 rounded-lg">
            No dietary alternatives found for this recipe.
          </div>
        )}
      </div>
    </RecipeCard>
  );
};
